import React, { useCallback, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { postData } from "../../utility/Utility";
import { Modal, Button, Form, InputGroup } from "react-bootstrap";
import CustomLoader from "../../Components/CustomLoader/CustomLoader";

function AdminProfile() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [profileData, setProfileData] = useState({});
    const [updateLoading, setUpdateLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [showEditModal, setShowEditModal] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [formData, setFormData] = useState({});
    const [passwordForm, setPasswordForm] = useState({
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
    });
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Add Font Awesome 5 CDN to head
    useEffect(() => {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = "https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.4/css/all.min.css";
        link.integrity = "sha512-1ycn6IcaQQ40/MKBW2W4Rhis/DbILU74C1vSrLJxCq57o941Ym01SwNsOMqvEBFlcgUa6xLiPY/NS5R+E6ztJQ==";
        link.crossOrigin = "anonymous";
        link.referrerPolicy = "no-referrer";
        document.head.appendChild(link);

        return () => {
            document.head.removeChild(link);
        };
    }, []);

    // Fetch admin profile data
    useEffect(() => {
        const fetchAdminProfile = async () => {
            try {
                setLoading(true);
                const response = await postData("api/admin/profile");
                if (response?.success) {
                    setProfileData(response.data);
                    setFormData(response.data);
                }
            } catch (error) {
                console.error("Failed to fetch admin profile:", error);
                setErrorMessage("Failed to load profile data");
            } finally {
                setLoading(false);
            }
        };

        fetchAdminProfile();
    }, []);

    // Helper functions with useCallback for optimization
    const getRoleBadgeColor = useCallback((role) => {
        const roleColors = {
            'super-admin': 'danger',
            admin: 'warning',
            moderator: 'primary',
            user: 'success',
        };
        return roleColors[role?.toLowerCase()] || 'secondary';
    }, []);

    const formatDate = useCallback((dateString) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: '2-digit',
            minute: '2-digit'
        });
    }, []);

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    // Handle password form changes
    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswordForm((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    // Toggle password visibility
    const togglePasswordVisibility = (field) => {
        switch (field) {
            case 'current':
                setShowCurrentPassword(!showCurrentPassword);
                break;
            case 'new':
                setShowNewPassword(!showNewPassword);
                break;
            case 'confirm':
                setShowConfirmPassword(!showConfirmPassword);
                break;
            default:
                break;
        }
    };

    // Handle profile update submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        setUpdateLoading(true);
        setErrorMessage("");
        setSuccessMessage("");

        try {
            const response = await postData("api/admin/update-profile", {
                username: formData.username,
                contactNumber: formData.contactNumber
            });

            if (response?.success) {
                setProfileData(response.data);
                setFormData(response.data);
                setShowEditModal(false);
                setSuccessMessage("Profile updated successfully!");
                setTimeout(() => setSuccessMessage(""), 3000);
            } else {
                setErrorMessage(response?.message || "Failed to update profile");
            }
        } catch (error) {
            console.error("Failed to update profile:", error);
            setErrorMessage("An error occurred while updating profile");
        } finally {
            setUpdateLoading(false);
        }
    };

    // Handle password change submission
    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        setUpdateLoading(true);
        setErrorMessage("");
        setSuccessMessage("");

        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            setErrorMessage("New passwords don't match");
            setUpdateLoading(false);
            return;
        }

        try {
            const response = await postData("api/admin/change-password", {
                currentPassword: passwordForm.currentPassword,
                newPassword: passwordForm.newPassword
            });

            if (response?.success) {
                setShowPasswordModal(false);
                setPasswordForm({
                    currentPassword: "",
                    newPassword: "",
                    confirmPassword: ""
                });
                setSuccessMessage("Password changed successfully!");
                setTimeout(() => setSuccessMessage(""), 3000);
            } else {
                setErrorMessage(response?.message || "Failed to change password");
            }
        } catch (error) {
            console.error("Failed to change password:", error);
            setErrorMessage("An error occurred while changing password");
        } finally {
            setUpdateLoading(false);
        }
    };

    if (loading) {
        return (
            <CustomLoader loading={loading} />
        );
    }

    return (
        <div className="content-page">
            <CustomLoader loading={loading} />
            <div className="content">
                <div className="container-fluid">
                    {/* Page Header */}
                    <div className="py-3 d-flex align-items-sm-center flex-sm-row flex-column justify-content-between">
                        <div className="flex-grow-1">
                            <div className="page-title-box">
                                <h4 className="mb-1">Admin Profile</h4>
                                <ol className="breadcrumb m-0">
                                    <li className="breadcrumb-item">Admin</li>
                                    <li className="breadcrumb-item active">Profile</li>
                                </ol>
                            </div>
                        </div>
                        <div className="d-flex gap-2">
                            <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={() => navigate(-1)}
                                aria-label="Go Back"
                            >
                                <i className="fas fa-arrow-left me-1"></i> Back
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => setShowEditModal(true)}
                            >
                                <i className="fas fa-edit me-1"></i> Edit Profile
                            </button>
                        </div>
                    </div>

                    {/* Success/Error Messages */}
                    {successMessage && (
                        <div className="alert alert-success alert-dismissible fade show" role="alert">
                            {successMessage}
                            <button type="button" className="btn-close" onClick={() => setSuccessMessage("")} aria-label="Close"></button>
                        </div>
                    )}
                    {errorMessage && (
                        <div className="alert alert-danger alert-dismissible fade show" role="alert">
                            {errorMessage}
                            <button type="button" className="btn-close" onClick={() => setErrorMessage("")} aria-label="Close"></button>
                        </div>
                    )}

                    {/* Profile Header */}
                    <div className="card mb-4 shadow-sm">
                        <div className="card-body d-flex flex-column flex-md-row align-items-center gap-3">
                            <div className="flex-grow-1 text-center text-md-start">
                                <h4 className="mb-2">{profileData.username || "N/A"}</h4>
                                <div className="d-flex flex-wrap gap-2 justify-content-center justify-content-md-start">
                                    <span className={`badge bg-${getRoleBadgeColor(profileData.role)} fs-6`}>
                                        {profileData.role?.toUpperCase() || "N/A"}
                                    </span>
                                    <span className="badge bg-success fs-6">
                                        Status: {profileData.isActive ? "Active" : "Inactive"}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Profile Sections */}
                    <div className="row">
                        {/* Account Information */}
                        <div className="col-md-6 mb-4">
                            <div className="card h-100">
                                <div className="card-header bg-light">
                                    <h5 className="mb-0">
                                        <i className="fas fa-user-circle me-2"></i>Account Information
                                    </h5>
                                </div>
                                <div className="card-body">
                                    <ul className="list-group list-group-flush">
                                        {/* Email */}
                                        <li className="list-group-item px-0 py-2">
                                            <strong>Email:</strong>
                                            <div className="text-muted">
                                                {profileData.email || "N/A"}
                                                {profileData.email && (
                                                    <a href={`mailto:${profileData.email}`} className="ms-2">
                                                        <i className="fas fa-envelope"></i>
                                                    </a>
                                                )}
                                            </div>
                                        </li>

                                        {/* Contact Number */}
                                        <li className="list-group-item px-0 py-2">
                                            <strong>Contact Number:</strong>
                                            <div className="text-muted">
                                                {profileData.contactNumber ? (
                                                    <a href={`tel:${profileData.contactNumber}`}>{profileData.contactNumber}</a>
                                                ) : (
                                                    "Not provided"
                                                )}
                                            </div>
                                        </li>

                                        {/* Role (non-editable) */}
                                        <li className="list-group-item px-0 py-2">
                                            <strong>Role:</strong>
                                            <div className="text-muted text-capitalize">
                                                {profileData.role?.replace('-', ' ') || "N/A"}
                                            </div>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* System Information */}
                        <div className="col-md-6 mb-4">
                            <div className="card h-100">
                                <div className="card-header bg-light">
                                    <h5 className="mb-0">
                                        <i className="fas fa-info-circle me-2"></i>System Information
                                    </h5>
                                </div>
                                <div className="card-body">
                                    <ul className="list-group list-group-flush">
                                        <li className="list-group-item px-0 py-2">
                                            <strong>User ID:</strong>
                                            <div className="text-muted text-truncate">{profileData._id || "N/A"}</div>
                                        </li>
                                        <li className="list-group-item px-0 py-2">
                                            <strong>Account Created:</strong>
                                            <div className="text-muted">
                                                {formatDate(profileData.createdAt)}
                                            </div>
                                        </li>
                                        <li className="list-group-item px-0 py-2">
                                            <strong>Last Updated:</strong>
                                            <div className="text-muted">
                                                {formatDate(profileData.updatedAt)}
                                            </div>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Security Section */}
                    <div className="row">
                        <div className="col-12 mb-4">
                            <div className="card">
                                <div className="card-header bg-light">
                                    <h5 className="mb-0">
                                        <i className="fas fa-shield-alt me-2"></i>Security
                                    </h5>
                                </div>
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <h6 className="mb-1">Password</h6>
                                            <p className="text-muted mb-0">Last changed: {formatDate(profileData.updatedAt)}</p>
                                        </div>
                                        <button
                                            className="btn btn-outline-warning"
                                            onClick={() => setShowPasswordModal(true)}
                                        >
                                            <i className="fas fa-key me-1"></i> Change Password
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Edit Profile Modal */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Edit Profile</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Username</Form.Label>
                            <Form.Control
                                type="text"
                                name="username"
                                value={formData.username || ""}
                                onChange={handleInputChange}
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Email</Form.Label>
                            <Form.Control
                                type="email"
                                name="email"
                                value={formData.email || ""}
                                onChange={handleInputChange}
                                disabled
                            />
                            <Form.Text className="text-muted">
                                Email cannot be changed for admin accounts
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Contact Number</Form.Label>
                            <Form.Control
                                type="tel"
                                name="contactNumber"
                                value={formData.contactNumber || ""}
                                onChange={handleInputChange}
                            />
                        </Form.Group>

                        {errorMessage && (
                            <div className="alert alert-danger mt-3">
                                {errorMessage}
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowEditModal(false)}>
                            <i className="fas fa-times me-1"></i> Cancel
                        </Button>
                        <Button variant="primary" type="submit" disabled={updateLoading}>
                            <i className="fas fa-save me-1"></i>
                            {updateLoading ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Change Password Modal */}
            <Modal show={showPasswordModal} onHide={() => setShowPasswordModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Change Password</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handlePasswordSubmit}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Current Password</Form.Label>
                            <InputGroup>
                                <Form.Control
                                    type={showCurrentPassword ? "text" : "password"}
                                    name="currentPassword"
                                    value={passwordForm.currentPassword}
                                    onChange={handlePasswordChange}
                                    required
                                />
                                <Button
                                    variant="outline-secondary"
                                    onClick={() => togglePasswordVisibility('current')}
                                >
                                    <i className={`fas ${showCurrentPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                </Button>
                            </InputGroup>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>New Password</Form.Label>
                            <InputGroup>
                                <Form.Control
                                    type={showNewPassword ? "text" : "password"}
                                    name="newPassword"
                                    value={passwordForm.newPassword}
                                    onChange={handlePasswordChange}
                                    required
                                    minLength={8}
                                />
                                <Button
                                    variant="outline-secondary"
                                    onClick={() => togglePasswordVisibility('new')}
                                >
                                    <i className={`fas ${showNewPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                </Button>
                            </InputGroup>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Confirm New Password</Form.Label>
                            <InputGroup>
                                <Form.Control
                                    type={showConfirmPassword ? "text" : "password"}
                                    name="confirmPassword"
                                    value={passwordForm.confirmPassword}
                                    onChange={handlePasswordChange}
                                    required
                                    minLength={8}
                                />
                                <Button
                                    variant="outline-secondary"
                                    onClick={() => togglePasswordVisibility('confirm')}
                                >
                                    <i className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                </Button>
                            </InputGroup>
                        </Form.Group>

                        {errorMessage && (
                            <div className="alert alert-danger mt-3">
                                {errorMessage}
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowPasswordModal(false)}>
                            <i className="fas fa-times me-1"></i> Cancel
                        </Button>
                        <Button variant="warning" type="submit" disabled={updateLoading}>
                            <i className="fas fa-key me-1"></i>
                            {updateLoading ? 'Updating...' : 'Change Password'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
}

export default AdminProfile;